import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import AppHeader from "../components/AppHeader.js";
import ScrollToTopButton from "../components/ScrollToTopButton.js";
import {
  GET,
  loading,
  Select,
  Pagination,
  toFixed,
  format_date,
  XLSX,
  saveAs,
} from "../components/CustomComponent.js";
import "../assets/css/bootstrap.css";
import "../assets/css/style.css";
import "../assets/css/manora-report.css";

// ตัวกรองสถานะกรองตามผล QC ของพาเลท (qc.status_id) — สถานะอื่นใน setting_status (Pending/Close/Active ...)
// ไม่มีทางตรงกับผล QC เลือกแล้วได้ตารางว่างเสมอ จึงแสดงเฉพาะสถานะผล QC
const QC_STATUS_FILTER_TITLES = ["Accept", "Special Approve", "Rework", "Hold", "Hold New Location", "Grade C", "Terminate"];

const PAGE_SIZE = 100000;

// จำนวนกล่อง/ชิ้น/ถุงของพาเลท (บรรจุ/FG/ทอด) — ข้อความเดียวกับหน้าค้นหาตำแหน่งในแอป ไม่มีเลยคืนค่าว่าง
const formatBoxPieces = (p) => {
  const parts = [];
  if (Number(p?.box_count || 0) > 0) parts.push(`${Number(p.box_count)} กล่อง`);
  if (Number(p?.piece_count || 0) > 0) parts.push(`${Number(p.piece_count)} ชิ้น`);
  if (Number(p?.bag_count || 0) > 0) parts.push(`${Number(p.bag_count)} ถุง`);
  return parts.join(" + ");
};

// (WEB-260826-1.5) ตัวกรอง "ประเภทสินค้า" ผูกกับ product_stage (ขั้นตอนการผลิตของพาเลท)
// ต้องตรงกับ 4 ค่าที่ resolvePalletProductType() ฝั่ง backend คืนกลับมาเป๊ะ
const PRODUCT_STAGE_OPTIONS = [
  { value: "อบรอคัด", labelKey: "stock.product_type_pending" },
  { value: "อบคัดแล้ว", labelKey: "stock.product_type_done" },
  { value: "ทอด", labelKey: "stock.product_type_fried" },
  { value: "บรรจุ", labelKey: "stock.product_type_packed" },
];

const PRODUCT_STAGE_BADGE_CLASS = {
  "อบรอคัด": "manora-badge-orange",
  "อบคัดแล้ว": "manora-badge-green",
  "ทอด": "manora-badge-cyan",
  "บรรจุ": "manora-badge-blue",
};

const PRODUCT_STAGE_LABEL_KEY = {
  "อบรอคัด": "stock.product_type_pending",
  "อบคัดแล้ว": "stock.product_type_done",
  "ทอด": "stock.product_type_fried",
  "บรรจุ": "stock.product_type_packed",
};

// (WEB-260826-1.6) code work center คงที่ทุกฐานข้อมูล (เหมือน storef1/storef5 ฝั่งคลัง)
// f1 = โรงอบ, f5 = โรงทอด — ใช้แยก dropdown ตู้อบ / เตาทอด ไม่ให้ปนกัน
const WORK_CENTER_OVEN = "f1";
const WORK_CENTER_FRY = "f5";

const cleanParams = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== "" && v !== null && v !== undefined));

const qcStatusBadge = (title) => {
  const map = {
    "Grade C": "manora-badge-cyan",
    "Terminate": "manora-badge-pink",
    "Special Approve": "manora-badge-green",
    "Rework": "manora-badge-orange",
    "Accept": "manora-badge-purple",
  };
  return map[title] || "manora-badge-gray";
};

class StockOnhand extends Component {
  state = {
    filters: {
      product_stage: "",
      warehouse_id: "",
      product_id: "",
      lot_no: "",
      grade_id: "",
      oven_id: "",
      fry_machine_id: "",
      status_id: "",
      search: "",
      // (STOCK=APP) ค่าเริ่มต้นแสดงของในคลังทั้งหมดเหมือนหน้าค้นหาตำแหน่งในแอป ติ๊กเพื่อซ่อนของที่รอรับเข้า
      hide_pending: false,
    },
    page: 1,
    report_date: "",
    summary: { lot_count: 0, product_count: 0, total_pallets: 0, total_weight: 0, avg_per_pallet: 0 },
    lots: [],
    pagination: null,

    products: [],
    warehouses: [],
    grades: [],
    ovenMachines: [],
    fryMachines: [],
    statuses: [],
  };

  async componentDidMount() {
    loading(true);
    const [productsRes, warehousesRes, gradesRes, ovenMachinesRes, fryMachinesRes, statusesRes] = await Promise.all([
      GET("/api/v1/internal/product", { page: 0 }),
      GET("/api/v1/internal/warehouse", { page: 0 }),
      GET("/api/v1/internal/grade", { page: 0 }),
      GET("/api/v1/internal/machine", { page: 0, work_center_code: WORK_CENTER_OVEN }),
      GET("/api/v1/internal/machine", { page: 0, work_center_code: WORK_CENTER_FRY }),
      GET("/api/v1/internal/status", { page: 0 }),
    ]);

    // (WEB-260826-1.6) work_center_code "f5" ผูกกับเครื่องจักรครบแล้ว (เช็คจาก DB dev) เลิกใช้ fallback
    // กรองจากชื่อ/รหัสที่มีคำว่า "เตา" เพราะเดาจากชื่อทำให้บั๊กแบบ dropdown ปนกันเงียบและหายาก
    const fryMachines = fryMachinesRes?.status === true ? fryMachinesRes.data : [];

    this.setState({
      products: productsRes?.status === true ? productsRes.data : [],
      warehouses: warehousesRes?.status === true ? warehousesRes.data : [],
      grades: gradesRes?.status === true ? gradesRes.data : [],
      ovenMachines: ovenMachinesRes?.status === true ? ovenMachinesRes.data : [],
      fryMachines,
      statuses: statusesRes?.status === true ? statusesRes.data : [],
    });

    await this.Get(1);
  }

  Get = async (page) => {
    loading(true);
    const { filters } = this.state;
    const params = cleanParams({ ...filters, hide_pending: filters.hide_pending ? "true" : "", page, page_size: PAGE_SIZE });
    const result = await GET("/api/v1/internal/report/stock-onhand", params);
    if (result?.status === true) {
      this.setState({
        page,
        report_date: result.data.report_date,
        summary: result.data.summary,
        lots: result.data.lots,
        pagination: result.pagination,
      });
    }
    loading(false);
  };

  updateFilter = (key, value) => {
    this.setState({ filters: { ...this.state.filters, [key]: value } }, () => this.Get(1));
  };

  // ช่องค้นหารวม: เก็บค่าที่พิมพ์ทันที แต่หน่วง 400ms ก่อนยิง API กันยิงทุกตัวอักษร
  onSearchChange = (value) => {
    this.setState({ filters: { ...this.state.filters, search: value } });
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.Get(1), 400);
  };

  componentWillUnmount() {
    clearTimeout(this.searchTimer);
  }

  ExportExcel = async () => {
    loading(true);
    const { filters } = this.state;
    const { t } = this.props;
    const params = cleanParams({ ...filters, hide_pending: filters.hide_pending ? "true" : "", page: 0 });
    const result = await GET("/api/v1/internal/report/stock-onhand", params);
    if (result?.status === true) {
      const rows = [];
      result.data.lots.forEach((lot) => {
        lot.pallets.forEach((p) => {
          rows.push({
            "LOT NO": lot.lot_no,
            เกรด: lot.grade?.name || "",
            "รายละเอียดของ ของเกรด": lot.description || "",
            // Lot เดียวมีได้หลายสินค้า (แผ่นดิบ/คัด/FG) — ใช้สินค้าของพาเลทแถวนั้น ไม่ใช่ของพาเลทแรกใน Lot
            สินค้า: p.product?.name || lot.product?.name || "",
            วันผลิต: p.production_date ? format_date(p.production_date) : "",
            คลัง: p.warehouse?.name || "",
            Location: p.location?.name || "",
            ประเภทสินค้า: p.product_type,
            รหัสสินค้า: p.product?.code || "",
            สถานะ: p.qc_status?.title || (p.pending_qc_status ? `${p.pending_qc_status.title} (${t("stock.status_wait_qc")})` : ""),
            ตู้อบ: p.oven?.name || "",
            เตาทอด: p.fry_machine?.name || "",
            "รหัส Pallet": p.pallet_no,
            "คงเหลือ (กก.)": p.amount,
            // พาเลทบรรจุ/FG นับเป็นกล่อง/ชิ้น (น้ำหนักอาจเป็น 0) — แสดงแบบหน้าค้นหาตำแหน่งในแอป
            "กล่อง/ชิ้น": formatBoxPieces(p),
          });
        });
      });
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "StockOnhand");
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(new Blob([buf], { type: "application/octet-stream" }), `stock-onhand-${format_date(new Date(), "yyyy-mm-dd")}.xlsx`);
    }
    loading(false);
  };

  render() {
    const { filters, summary, lots, pagination, products, warehouses, grades, ovenMachines, fryMachines, statuses, report_date } =
      this.state;
    const { t } = this.props;

    return (
      <div className="w-100 min-vh-100" style={{ background: "#f5f3fa" }}>
        <AppHeader title={t("stock.title")} subtitle={t("stock.subtitle")} activeTab="stock" />

        <div className="p-3">
          <div className="manora-filter-card d-flex flex-wrap align-items-end mb-3" style={{ gap: 10 }}>
            <div style={{ minWidth: 140 }}>
              <div className="text-12 text-secondary mb-1">{t("stock.filter_type")}</div>
              <Select
                className="form-control"
                options={PRODUCT_STAGE_OPTIONS.map((s) => ({ value: s.value, label: t(s.labelKey) }))}
                value={filters.product_stage}
                onChange={(v) => this.updateFilter("product_stage", v)}
              />
            </div>
            <div style={{ minWidth: 140 }}>
              <div className="text-12 text-secondary mb-1">{t("stock.filter_warehouse")}</div>
              <Select
                className="form-control"
                options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
                value={filters.warehouse_id}
                onChange={(v) => this.updateFilter("warehouse_id", v)}
              />
            </div>
            <div style={{ minWidth: 160 }}>
              <div className="text-12 text-secondary mb-1">{t("stock.filter_product")}</div>
              <Select
                className="form-control"
                options={products.map((p) => ({ value: p.id, label: p.alias || p.name }))}
                value={filters.product_id}
                onChange={(v) => this.updateFilter("product_id", v)}
              />
            </div>
            <div style={{ minWidth: 140 }}>
              <div className="text-12 text-secondary mb-1">{t("stock.filter_lot")}</div>
              <input
                className="form-control"
                placeholder={t("stock.filter_lot_placeholder")}
                value={filters.lot_no}
                onChange={(e) => this.updateFilter("lot_no", e.target.value)}
              />
            </div>
            <div style={{ minWidth: 110 }}>
              <div className="text-12 text-secondary mb-1">{t("stock.filter_grade")}</div>
              <Select
                className="form-control"
                options={grades.map((g) => ({ value: g.id, label: g.name }))}
                value={filters.grade_id}
                onChange={(v) => this.updateFilter("grade_id", v)}
              />
            </div>
            <div style={{ minWidth: 130 }}>
              <div className="text-12 text-secondary mb-1">{t("stock.filter_oven")}</div>
              <Select
                className="form-control"
                options={ovenMachines.map((m) => ({ value: m.id, label: m.name }))}
                value={filters.oven_id}
                onChange={(v) => this.updateFilter("oven_id", v)}
              />
            </div>
            <div style={{ minWidth: 130 }}>
              <div className="text-12 text-secondary mb-1">{t("stock.filter_fry")}</div>
              <Select
                className="form-control"
                options={fryMachines.map((m) => ({ value: m.id, label: m.name }))}
                value={filters.fry_machine_id}
                onChange={(v) => this.updateFilter("fry_machine_id", v)}
              />
            </div>
            <div style={{ minWidth: 140 }}>
              <div className="text-12 text-secondary mb-1">{t("stock.filter_status")}</div>
              <Select
                className="form-control"
                options={statuses
                  .filter((s) => QC_STATUS_FILTER_TITLES.includes(s.title))
                  .map((s) => ({ value: s.id, label: s.title }))}
                value={filters.status_id}
                onChange={(v) => this.updateFilter("status_id", v)}
              />
            </div>
            <div style={{ minWidth: 180 }}>
              <div className="text-12 text-secondary mb-1">{t("stock.filter_search")}</div>
              <input
                className="form-control"
                placeholder={t("stock.filter_search_placeholder")}
                value={filters.search}
                onChange={(e) => this.onSearchChange(e.target.value)}
              />
            </div>
            <div className="form-check" style={{ minWidth: 200 }}>
              <input
                className="form-check-input"
                type="checkbox"
                id="stock-include-pending"
                checked={filters.hide_pending}
                onChange={(e) => this.updateFilter("hide_pending", e.target.checked)}
              />
              <label className="form-check-label text-12" htmlFor="stock-include-pending">
                {t("stock.filter_hide_pending")}
              </label>
            </div>
            <button className="btn btn-success" onClick={() => this.ExportExcel()}>
              {t("stock.export_excel")}
            </button>
          </div>

          <div className="row g-2 mb-3">
            <div className="col-6 col-md-3">
              <div className="manora-summary-card border-purple h-100">
                <div className="manora-summary-label">{t("stock.summary_lot_count")}</div>
                <div className="manora-summary-value">
                  {summary.lot_count} {t("stock.summary_lot_unit")}
                </div>
                <div className="manora-summary-sub">
                  {summary.product_count} {t("stock.summary_product_count_suffix")}
                </div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="manora-summary-card border-blue h-100">
                <div className="manora-summary-label">{t("stock.summary_pallet_total")}</div>
                <div className="manora-summary-value">
                  {summary.total_pallets} {t("stock.summary_pallet_unit")}
                </div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="manora-summary-card border-green h-100">
                <div className="manora-summary-label">{t("stock.summary_weight_total")}</div>
                <div className="manora-summary-value">{toFixed(summary.total_weight)} กก.</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="manora-summary-card border-orange h-100">
                <div className="manora-summary-label">{t("stock.summary_avg_pallet")}</div>
                <div className="manora-summary-value">{toFixed(summary.avg_per_pallet)} กก.</div>
              </div>
            </div>
          </div>

          <div className="manora-card">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="fw-bold">
                {t("stock.list_title")} {report_date ? format_date(report_date) : "-"}
              </div>
              <small className="text-secondary">
                {summary.total_pallets} {t("stock.summary_pallet_unit")} / {summary.lot_count} {t("stock.summary_lot_unit")}
              </small>
            </div>
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <td className="text-nowrap">{t("stock.col_warehouse")}</td>
                    <td className="text-nowrap">{t("stock.col_location")}</td>
                    <td className="text-nowrap">{t("stock.col_product_type")}</td>
                    <td className="text-nowrap">{t("stock.col_product_code")}</td>
                    <td className="text-nowrap">{t("stock.col_product_name")}</td>
                    <td className="text-nowrap">{t("stock.col_lot_no")}</td>
                    <td className="text-nowrap">{t("stock.col_status")}</td>
                    <td className="text-nowrap">{t("stock.col_oven")}</td>
                    <td className="text-nowrap">{t("stock.col_fry")}</td>
                    <td className="text-nowrap">{t("stock.col_production_date")}</td>
                    <td className="text-nowrap">{t("stock.col_pallet_no")}</td>
                    <td className="text-nowrap text-end">{t("stock.col_remaining")}</td>
                  </tr>
                </thead>
                <tbody>
                  {lots.map((lot, li) => (
                    <React.Fragment key={`lot-${li}`}>
                      <tr className="manora-lot-header">
                        <td className="text-nowrap" colSpan={12}>
                          <b>{lot.lot_no}</b>{" "}
                          {/* กลุ่มเป็น Lot + สินค้า (แบบแอป) — Lot เดียวกันมีได้หลายกลุ่ม ต้องบอกสินค้าที่หัวกลุ่ม */}
                          {lot.product && (
                            <span className="text-secondary mx-1">
                              {lot.product.code ? `${lot.product.code} · ` : ""}
                              {lot.product.name}
                            </span>
                          )}
                          <span className="manora-badge manora-badge-green mx-1">{lot.grade ? lot.grade.name : "-"}</span>
                          <span className="mx-1">{lot.description || "-"}</span>
                          <div className="text-12 text-secondary">
                            {t("stock.lot_meta", {
                              date: lot.production_date ? format_date(lot.production_date) : "-",
                              count: lot.pallet_count,
                            })}
                          </div>
                        </td>
                      </tr>
                      {lot.pallets.map((p) => (
                        <tr key={`pallet-${p.id}`}>
                          <td className="text-nowrap">{p.warehouse?.name}</td>
                          <td className="text-nowrap">{p.location?.name}</td>
                          <td className="text-nowrap">
                            <span
                              className={`manora-badge ${PRODUCT_STAGE_BADGE_CLASS[p.product_type] || "manora-badge-gray"}`}
                            >
                              {PRODUCT_STAGE_LABEL_KEY[p.product_type] ? t(PRODUCT_STAGE_LABEL_KEY[p.product_type]) : p.product_type}
                            </span>
                          </td>
                          <td className="manora-link-text text-nowrap">{p.product?.code}</td>
                          <td className="text-nowrap">{p.product?.name}</td>
                          <td className="manora-link-text text-nowrap">
                            {p.batch_no}
                            {p.is_pending && (
                              <span className="manora-badge manora-badge-orange mx-1">{t("stock.badge_pending")}</span>
                            )}
                          </td>
                          <td>
                            {p.qc_status && (
                              <span className={`manora-badge ${qcStatusBadge(p.qc_status.title)}`}>{p.qc_status.title}</span>
                            )}
                            {/* ยังไม่ผ่าน QC แต่ตัวพาเลทมีสถานะแล้ว (คัดแยก/ของคืน) — บอกสถานะพร้อม "รอ QC" */}
                            {!p.qc_status && p.pending_qc_status && (
                              <span className="manora-badge manora-badge-orange">
                                {p.pending_qc_status.title} ({t("stock.status_wait_qc")})
                              </span>
                            )}
                          </td>
                          <td className="text-nowrap">{p.oven?.name || "-"}</td>
                          <td className="text-nowrap">{p.fry_machine?.name || "-"}</td>
                          <td className="text-nowrap">{p.production_date ? format_date(p.production_date) : "-"}</td>
                          <td className="text-nowrap">{p.pallet_no}</td>
                          <td className="text-end fw-bold text-nowrap">
                            {toFixed(p.amount)}
                            {formatBoxPieces(p) && <div className="text-12 text-secondary fw-normal">{formatBoxPieces(p)}</div>}
                          </td>
                        </tr>
                      ))}
                      <tr className="manora-lot-subtotal">
                        <td colSpan={11} className="text-end">
                          {t("stock.subtotal", { lot: lot.lot_no, count: lot.pallet_count })}
                        </td>
                        <td className="text-end text-nowrap">{toFixed(lot.total_amount)} กก.</td>
                      </tr>
                    </React.Fragment>
                  ))}
                  {lots.length === 0 && (
                    <tr>
                      <td colSpan={12} className="text-center text-secondary py-4">
                        {t("stock.no_data")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="w-100 d-flex align-items-center justify-content-end flex-wrap mt-2">
              <span className="mb-3 me-3 text-secondary">
                {pagination
                  ? t("stock.pagination_showing", {
                      from: pagination.start_index + 1,
                      to: pagination.end_index,
                      total: pagination.count,
                    })
                  : ""}
              </span>
              <Pagination
                className="mb-3"
                count={Math.ceil((pagination?.count || 0) / PAGE_SIZE) || 1}
                page={this.state.page}
                showFirstButton
                showLastButton
                onChange={(_, page) => this.Get(page)}
              />
            </div>
          </div>
        </div>
        <ScrollToTopButton />
      </div>
    );
  }
}

export default withTranslation()(StockOnhand);
